// Modelo de capacidades por tenant (ADR 2026-05-21-unificacion-capabilities).
//
// Capas desacopladas:
//   - plan: preset comercial (nombre) → asigna un set de capabilities.
//   - capabilities: flags técnicos. EL GATING DEL CÓDIGO LEE ESTO, no el plan.
//   - region: CL/US del tenant → deriva la moneda (CLP/USD), fija, no elegible.
//   - billing (pagos automáticos): diferido al Bloque 7.
//
// Esta es la única fuente de verdad del mapa preset→capabilities en el admin.
// (La migración PB replica el mapa inline porque corre en el VM de Go.)

export const CAPABILITY_KEYS = [
  'crm',
  'cms',
  'forms',
  'leads_inbox',
  'chatbot',
  'analytics_traffic',
  'ads_google',
  'ads_meta',
  'social',
] as const;

export type Capability = (typeof CAPABILITY_KEYS)[number];
export type TenantCapabilities = Partial<Record<Capability, boolean>>;

/**
 * Slug del plan. Los 4 presets comerciales + `custom` (caps editadas a mano).
 * La collection PB `plans` es la fuente viva de presets; estos values son
 * fallback + convención de naming comercial (Focus CRM / Completo / …).
 */
export type PlanPreset = string;

/** Plan cuando las capabilities no matchean ningún preset. No es vendible. */
export const CUSTOM_PLAN_SLUG = 'custom';

/** Fallback hardcoded — usado cuando el cache de `plans` no está disponible. */
/** Plan que prende todas las capacidades. El cliente lo ve como "Completo". */
export const COMPLETO_PLAN_SLUG = 'completo';

export const PLAN_PRESETS: Record<string, Capability[]> = {
  'crm-base': ['crm', 'leads_inbox', 'forms', 'chatbot'],
  'web-cms': ['cms', 'forms', 'leads_inbox'],
  'sistema-focus': ['crm', 'cms', 'forms', 'leads_inbox', 'chatbot', 'analytics_traffic'],
  'sistema-focus-ads': [...CAPABILITY_KEYS],
  [COMPLETO_PLAN_SLUG]: [...CAPABILITY_KEYS],
};

/** Fallback hardcoded de labels comerciales. Las labels vivas vienen de `usePlans()`. */
export const PLAN_LABELS: Record<string, string> = {
  'crm-base': 'Focus CRM',
  'web-cms': 'Web + CMS',
  'sistema-focus': 'Focus Completo',
  'sistema-focus-ads': 'Focus Completo + Ads',
  [COMPLETO_PLAN_SLUG]: 'Completo',
  [CUSTOM_PLAN_SLUG]: 'Custom',
};

/** Fallback hardcoded de orden (presets vendibles; sin custom). */
export const PLAN_ORDER: string[] = [
  'crm-base',
  'web-cms',
  'sistema-focus',
  'sistema-focus-ads',
  COMPLETO_PLAN_SLUG,
];

// Etiquetas legibles + agrupación para el editor de capacidades del tenant.
export const CAPABILITY_LABELS: Record<Capability, string> = {
  crm: 'CRM (oportunidades, pipeline, tareas, inbox)',
  cms: 'Sitio web (gestionado)',
  forms: 'Formularios',
  leads_inbox: 'Bandeja de oportunidades',
  chatbot: 'Chatbot',
  analytics_traffic: 'Estadísticas de tráfico',
  ads_google: 'Google Ads',
  ads_meta: 'Meta Ads',
  social: 'Redes sociales',
};

// Chips cortos para el overview de la lista de tenants.
export const CAPABILITY_SHORT: Record<Capability, string> = {
  crm: 'CRM',
  cms: 'CMS',
  forms: 'Forms',
  leads_inbox: 'Oport.',
  chatbot: 'Bot',
  analytics_traffic: 'Stats',
  ads_google: 'GAds',
  ads_meta: 'Meta',
  social: 'Social',
};

// Región del tenant + moneda DERIVADA. Una sola fuente de verdad (region):
// la moneda nunca se elige aparte, así es imposible mezclar CLP y USD dentro
// de un mismo tenant. Los chilenos operan en CLP, los de USA en USD.
// (El billing/pagos automáticos se difiere al Bloque 7.)
export type Region = 'CL' | 'US';
export type TenantCurrency = 'CLP' | 'USD';

export const REGION_CURRENCY: Record<Region, TenantCurrency> = {
  CL: 'CLP',
  US: 'USD',
};

export const REGION_LABELS: Record<Region, string> = {
  CL: 'Chile · CLP',
  US: 'Estados Unidos · USD',
};

export const REGION_ORDER: Region[] = ['CL', 'US'];

/** Moneda oficial del tenant, derivada de su región. No es elegible. */
export function tenantCurrency(
  t: { region?: Region | string | null } | null | undefined,
): TenantCurrency {
  const r = (t?.region as Region) ?? 'CL';
  return REGION_CURRENCY[r] ?? 'CLP';
}

/**
 * Moneda a mostrar para una oportunidad: la del lead si viene seteada,
 * si no la del tenant (nunca USD por defecto — eso confundía a clientes CL).
 */
export function leadCurrency(
  lead: { currency?: string | null } | null | undefined,
  tenant: { region?: Region | string | null } | null | undefined,
): TenantCurrency | string {
  const c = lead?.currency?.trim();
  if (c) return c;
  return tenantCurrency(tenant);
}

/** Formatea un monto en la moneda dada (es-CL). */
export function formatMoney(
  value: number | null | undefined,
  currency: string,
  empty = '—',
): string {
  if (value == null || Number.isNaN(value)) return empty;
  try {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${currency} ${value.toLocaleString('es-CL')}`;
  }
}

/** Formatea un monto con la moneda del tenant. */
export function formatMoneyForTenant(
  tenant: { region?: Region | string | null } | null | undefined,
  value: number | null | undefined,
  empty = '—',
): string {
  return formatMoney(value, tenantCurrency(tenant), empty);
}

type PlanCapsSource = ReadonlyArray<{
  slug: string;
  capabilities?: Capability[] | null;
  archived?: boolean;
}>;

/** ¿Las caps del tenant matchean exactamente el preset del plan? */
export function capabilitiesMatchPreset(
  caps: TenantCapabilities | null | undefined,
  plan: string,
  plans?: PlanCapsSource,
): boolean {
  if (!plan || plan === CUSTOM_PLAN_SLUG) return false;
  const expected = presetCapabilities(plan, plans);
  for (const k of CAPABILITY_KEYS) {
    if (!!caps?.[k] !== !!expected[k]) return false;
  }
  return true;
}

/**
 * Tras editar capabilities a mano: si coinciden con un preset → ese plan;
 * si no → `custom`. Al elegir un plan del select se aplica el preset y se
 * deja ese slug (no pasa por acá).
 */
export function resolvePlanFromCapabilities(
  caps: TenantCapabilities | null | undefined,
  plans?: PlanCapsSource,
): string {
  const candidates =
    plans && plans.length > 0
      ? plans.filter((p) => !p.archived && p.slug !== CUSTOM_PLAN_SLUG).map((p) => p.slug)
      : PLAN_ORDER;
  for (const slug of candidates) {
    if (capabilitiesMatchPreset(caps, slug, plans)) return slug;
  }
  return CUSTOM_PLAN_SLUG;
}

/** Id de registro de PocketBase (15 caracteres). No es un slug de plan. */
function looksLikeRecordId(value: string): boolean {
  return /^[a-z0-9]{15}$/.test(value);
}

/** Label de plan para badges/tablas. Acepta slug o id del registro en `plans`. */
export function planDisplayLabel(
  slugOrId: string | null | undefined,
  plans?: ReadonlyArray<{ id?: string; slug: string; label?: string }>,
): string {
  if (!slugOrId) return '—';
  const found = plans?.find((p) => p.id === slugOrId || p.slug === slugOrId);
  if (found?.label) return found.label;
  const slug = found?.slug ?? slugOrId;
  if (PLAN_LABELS[slug]) return PLAN_LABELS[slug];
  if (!found && looksLikeRecordId(slugOrId)) return '—';
  return slug;
}

// ── Zona horaria del tenant ─────────────────────────────────────────────────
// Define a qué hora LOCAL del negocio disparan los recordatorios `scheduled`
// (tareas por vencer/vencidas). El backend (scheduler) lee este campo para
// convertir la hora configurada en el flow a la hora UTC real del cron.
export const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: 'America/Santiago', label: 'Chile (Santiago)' },
  { value: 'America/New_York', label: 'EE.UU. — Este (New York)' },
  { value: 'America/Chicago', label: 'EE.UU. — Central (Chicago)' },
  { value: 'America/Denver', label: 'EE.UU. — Montaña (Denver)' },
  { value: 'America/Los_Angeles', label: 'EE.UU. — Pacífico (Los Angeles)' },
  { value: 'America/Phoenix', label: 'EE.UU. — Arizona (sin horario de verano)' },
  { value: 'UTC', label: 'UTC' },
];

/** Default razonable de zona horaria según la región del tenant. */
export function defaultTimezone(region: Region | string | null | undefined): string {
  return region === 'US' ? 'America/New_York' : 'America/Santiago';
}

/**
 * Capabilities derivadas de un preset (todas las keys, true/false explícito).
 *
 * `plans` opcional: si se pasa, busca por slug en esa lista (data viva del PB).
 * Si no se encuentra o no se pasa, usa el hardcoded `PLAN_PRESETS` como fallback.
 * Los hooks del editor de /clientes y /planes deben pasar `plans` desde usePlans()
 * para que reflejen ediciones recientes; código no-React puede llamar sin él.
 */
/** Marca o desmarca todas. "Todas" deja el plan Completo. */
export function applyCapabilityBulk(mode: 'all' | 'none'): {
  capabilities: TenantCapabilities;
  plan: string;
} {
  if (mode === 'none') {
    const capabilities: TenantCapabilities = {};
    for (const key of CAPABILITY_KEYS) capabilities[key] = false;
    return { capabilities, plan: CUSTOM_PLAN_SLUG };
  }
  return {
    capabilities: presetCapabilities(COMPLETO_PLAN_SLUG),
    plan: COMPLETO_PLAN_SLUG,
  };
}

/**
 * El selector de planes vive en PocketBase. Si esa lista todavía no trae
 * Completo, lo agregamos para poder prender todas las capacidades.
 */
export function ensureCompletoOption<T extends { slug: string; label: string }>(options: T[]): T[] {
  if (options.some((option) => option.slug === COMPLETO_PLAN_SLUG)) return options;
  return [...options, { slug: COMPLETO_PLAN_SLUG, label: PLAN_LABELS[COMPLETO_PLAN_SLUG] } as T];
}

export function presetCapabilities(
  plan: PlanPreset,
  plans?: ReadonlyArray<{ slug: string; capabilities?: Capability[] | null }>,
): TenantCapabilities {
  let caps: Capability[] | null = null;
  if (plans) {
    const found = plans.find((p) => p.slug === plan);
    if (found?.capabilities && Array.isArray(found.capabilities)) {
      caps = found.capabilities;
    }
  }
  if (!caps) caps = PLAN_PRESETS[plan] ?? [];
  const on = new Set<Capability>(caps);
  const out: TenantCapabilities = {};
  for (const k of CAPABILITY_KEYS) out[k] = on.has(k);
  return out;
}

/**
 * Fuente de verdad del gating. Lee `capabilities`; si falta (backfill viejo),
 * deriva del preset del plan como fallback defensivo.
 */
export function tenantHasCapability(
  t: { capabilities?: TenantCapabilities | null; plan?: string } | null | undefined,
  cap: Capability,
): boolean {
  if (!t) return false;
  const caps = t.capabilities;
  if (caps && typeof caps[cap] === 'boolean') return !!caps[cap];
  const preset = PLAN_PRESETS[(t.plan ?? '') as PlanPreset];
  return preset ? preset.includes(cap) : false;
}

// ── Gating de rutas de la vista-tenant ───────────────────────────────────
// Mapea cada path del tenant a la capability que lo habilita. null = siempre.
// Un array = basta con tener CUALQUIERA de esas capabilities (OR).
// El gating del sidebar y el guard de rutas leen ESTO.
export const TENANT_PATH_CAPABILITY: Record<string, Capability | Capability[] | null> = {
  '/': 'crm',
  '/inbox': 'crm',
  '/crm': 'crm',
  '/oportunidades': 'crm',
  '/tasks': 'crm',
  '/agenda': 'crm',
  '/contacts': 'crm',
  // Reportes hospeda 3 pestañas con gating propio: Conversiones (crm),
  // Analítica web (analytics_traffic) y Google Ads (ads_google). Basta con
  // tener cualquiera de ellas para ver la página.
  '/reports': ['crm', 'analytics_traffic', 'ads_google'],
  '/cms': 'cms',
  '/oportunidades-bandeja': 'leads_inbox',
  '/settings': null,
};

/** ¿El tenant puede acceder a este path de la vista-tenant? */
export function tenantCanAccessPath(
  t: { capabilities?: TenantCapabilities | null; plan?: string } | null | undefined,
  path: string,
): boolean {
  // Match por path base (ej '/crm/leads/123' → '/crm'; '/' queda '/').
  const base = path === '/' ? '/' : '/' + (path.split('/')[1] ?? '');
  const cap = TENANT_PATH_CAPABILITY[base];
  if (cap == null) return true; // undefined (no mapeado) o null (siempre)
  if (Array.isArray(cap)) return cap.some((c) => tenantHasCapability(t, c));
  return tenantHasCapability(t, cap);
}

/** Home al que cae el tenant según sus capabilities. */
export function tenantHomePath(
  t: { capabilities?: TenantCapabilities | null; plan?: string } | null | undefined,
): string {
  if (tenantHasCapability(t, 'crm')) return '/';
  if (tenantHasCapability(t, 'cms')) return '/cms';
  if (tenantHasCapability(t, 'leads_inbox')) return '/oportunidades-bandeja';
  return '/settings';
}
